import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog.js';
import {
  isLoseAccessRefusal,
  useAudienceCandidates,
  useChangeAudiencePrincipal,
  useDashboardAudience,
  useSetAudienceRestricted,
  type AudiencePrincipal,
  type AudienceRoleCandidate,
  type AudienceUserCandidate,
} from '../../api/dashboards.js';

/**
 * CSAGE-557: "Manage access" for one dashboard under the board audience model.
 *
 * Off ("Everyone in the customer"): everyone in the board's customer who can
 * view dashboards sees it. On ("Only selected roles and users"): only the
 * listed roles and users do. Adding a role or user turns it on (CSAGE-581).
 * Principals are kept when it is turned off again, and apply once it is back
 * on.
 *
 * CSAGE-581: a change that would hide the board from the person making it
 * (they are in none of the listed roles or users) is refused by the API until
 * they confirm, so nobody locks themselves out by accident.
 *
 * The pickers ask the API, which searches the Canopy user-service with the
 * caller's own sign-in (CSAGE-558): only roles and users they may see, in the
 * board's customer, that can view dashboards. Access is granted per dashboard;
 * folders are not shared (they show when they hold a board the user can see).
 */
interface Props {
  dashboardId: string;
  dashboardName: string;
  onClose(): void;
}

type PickerKind = 'roles' | 'users';

const TYPE_LABEL: Record<AudiencePrincipal['type'], string> = {
  role: 'Role',
  user: 'User',
  path: 'Location',
};

/** Wait this long after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 300;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function ManageAccessDialog({ dashboardId, dashboardName, onClose }: Props): JSX.Element {
  const audienceQ = useDashboardAudience(dashboardId);
  const setRestricted = useSetAudienceRestricted(dashboardId);
  const add = useChangeAudiencePrincipal(dashboardId, 'add');
  const remove = useChangeAudiencePrincipal(dashboardId, 'remove');
  const [kind, setKind] = useState<PickerKind>('roles');
  const [search, setSearch] = useState('');
  const query = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const [error, setError] = useState<string | null>(null);
  /** A change the API refused because it would hide the board from the caller. */
  const [loseAccess, setLoseAccess] = useState<(() => Promise<unknown>) | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // The lose-access confirmation handles its own Escape.
      if (e.key === 'Escape' && !loseAccess) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, loseAccess]);

  /**
   * Run a change. If the API says it would hide the board from the caller,
   * ask them to confirm, then resend it with `confirmLoseAccess`.
   */
  async function run(action: (confirmLoseAccess?: boolean) => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await action();
    } catch (err) {
      if (isLoseAccessRefusal(err)) setLoseAccess(() => () => action(true));
      else setError((err as Error).message);
    }
  }

  const audience = audienceQ.data;
  const busy = setRestricted.isPending || add.isPending || remove.isPending;
  const added = new Set((audience?.principals ?? []).map((p) => `${p.type}:${p.id}`));

  return (
    <>
      <div className="modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="modal modal--detail manage-access-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="manage-access-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <div>
              <h2 id="manage-access-title">Manage access</h2>
              <p className="modal-subtitle">{dashboardName}</p>
            </div>
            <button type="button" className="btn-icon" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="modal-body">
            {audienceQ.isLoading ? (
              <p className="text-dim" role="status">
                Loading access…
              </p>
            ) : audienceQ.isError || !audience ? (
              <div className="alert alert--danger" role="alert">
                Couldn’t load who can see this dashboard.{' '}
                {audienceQ.error ? (audienceQ.error as Error).message : ''}{' '}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => void audienceQ.refetch()}
                >
                  Try again
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="alert alert--danger" role="alert">
                    {error}
                  </div>
                )}

                <fieldset className="field manage-access-mode" disabled={busy}>
                  <legend className="field-label">Who can see this dashboard</legend>
                  <label className="manage-access-option">
                    <input
                      type="radio"
                      name="manage-access-mode"
                      checked={!audience.restricted}
                      onChange={() =>
                        void run(() => setRestricted.mutateAsync({ restricted: false }))
                      }
                    />
                    <span>
                      <strong>Everyone in the customer</strong>
                      <span className="field-help">
                        Everyone in this customer who can view dashboards.
                      </span>
                    </span>
                  </label>
                  <label className="manage-access-option">
                    <input
                      type="radio"
                      name="manage-access-mode"
                      checked={audience.restricted}
                      onChange={() =>
                        void run((confirmLoseAccess) =>
                          setRestricted.mutateAsync({ restricted: true, confirmLoseAccess }),
                        )
                      }
                    />
                    <span>
                      <strong>Only selected roles and users</strong>
                      <span className="field-help">Only the roles and users listed below.</span>
                    </span>
                  </label>
                </fieldset>

                {audience.restricted && audience.principals.length === 0 && (
                  <div className="alert alert--warning" role="status">
                    Nobody in this customer can see this dashboard until you add a role or user.
                  </div>
                )}

                <section className="manage-access-section" aria-labelledby="manage-access-current">
                  <h3 id="manage-access-current">Roles and users with access</h3>
                  {!audience.restricted && audience.principals.length > 0 && (
                    <p className="field-help">
                      Kept for later. They apply once you choose “Only selected roles and users”.
                    </p>
                  )}
                  {audience.principals.length === 0 ? (
                    <p className="text-dim">No roles or users added yet.</p>
                  ) : (
                    <ul className="manage-access-list">
                      {audience.principals.map((p) => (
                        <li key={`${p.type}:${p.id}`} className="manage-access-row">
                          <span className="badge badge--neutral">{TYPE_LABEL[p.type]}</span>
                          <span className="manage-access-label">{p.label ?? p.id}</span>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={busy}
                            aria-label={`Remove ${p.label ?? p.id}`}
                            onClick={() =>
                              void run((confirmLoseAccess) =>
                                remove.mutateAsync({ type: p.type, id: p.id, confirmLoseAccess }),
                              )
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="manage-access-section" aria-labelledby="manage-access-add">
                  <h3 id="manage-access-add">Add</h3>
                  {!audience.restricted && (
                    <p className="field-help">
                      Adding a role or user switches this dashboard to “Only selected roles and
                      users”.
                    </p>
                  )}
                  <div className="tabs" role="tablist" aria-label="Add a role or a user">
                    {(['roles', 'users'] as const).map((k) => (
                      <button
                        key={k}
                        type="button"
                        role="tab"
                        aria-selected={kind === k}
                        className={'tab' + (kind === k ? ' tab--active' : '')}
                        onClick={() => setKind(k)}
                      >
                        {k === 'roles' ? 'Roles' : 'Users'}
                      </button>
                    ))}
                  </div>
                  <label className="field">
                    <span className="field-label">
                      {kind === 'roles' ? 'Search roles by name' : 'Search users by email or name'}
                    </span>
                    <input
                      className="input"
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      maxLength={100}
                    />
                  </label>
                  <CandidateList
                    dashboardId={dashboardId}
                    kind={kind}
                    query={query}
                    added={added}
                    busy={busy}
                    onAdd={(type, id) =>
                      void run((confirmLoseAccess) =>
                        add.mutateAsync({ type, id, confirmLoseAccess }),
                      )
                    }
                  />
                </section>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
      {loseAccess && (
        <ConfirmDialog
          title="You will lose access to this dashboard"
          message={
            <p>
              You are not in any of the roles or users this dashboard would be restricted to. If you
              continue, you will no longer see it or be able to change who can. An internal admin
              can give you access again.
            </p>
          }
          confirmLabel="Continue"
          onConfirm={async () => {
            await loseAccess();
            setLoseAccess(null);
            // The board is now hidden from this user, so there is nothing left to manage.
            onClose();
          }}
          onCancel={() => setLoseAccess(null)}
        />
      )}
    </>
  );
}

function CandidateList({
  dashboardId,
  kind,
  query,
  added,
  busy,
  onAdd,
}: {
  dashboardId: string;
  kind: PickerKind;
  query: string;
  added: ReadonlySet<string>;
  busy: boolean;
  onAdd(type: 'role' | 'user', id: string): void;
}): JSX.Element {
  const q = useAudienceCandidates(dashboardId, kind, query);
  if (q.isLoading) {
    return (
      <p className="text-dim" role="status">
        Searching…
      </p>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="alert alert--danger" role="alert">
        {q.error ? (q.error as Error).message : `Couldn’t search ${kind}.`}
      </div>
    );
  }
  const type = kind === 'roles' ? 'role' : 'user';
  if (q.data.items.length === 0) {
    return (
      <p className="text-dim">
        {query
          ? `No ${kind} match “${query}”.`
          : `No ${kind} in this customer can view dashboards.`}
      </p>
    );
  }
  return (
    <>
      <ul className="manage-access-list">
        {q.data.items.map((c) => {
          const isAdded = added.has(`${type}:${c.id}`);
          const { title, detail } =
            kind === 'roles'
              ? describeRole(c as AudienceRoleCandidate)
              : describeUser(c as AudienceUserCandidate);
          return (
            <li key={c.id} className="manage-access-row">
              <span className="manage-access-label">
                {title}
                <span className="field-help">{detail}</span>
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={busy || isAdded}
                aria-label={isAdded ? `${title} added` : `Add ${title}`}
                onClick={() => onAdd(type, c.id)}
              >
                {isAdded ? 'Added' : 'Add'}
              </button>
            </li>
          );
        })}
      </ul>
      {q.data.truncated && (
        <p className="field-help">Showing the first matches. Search to narrow the list.</p>
      )}
    </>
  );
}

function describeRole(r: AudienceRoleCandidate): { title: string; detail: string } {
  const users =
    r.userCount === null ? '–' : `${r.userCount} ${r.userCount === 1 ? 'user' : 'users'}`;
  return { title: r.name, detail: `${r.path} · ${users}` };
}

function describeUser(u: AudienceUserCandidate): { title: string; detail: string } {
  const title = u.name ?? u.email ?? u.username ?? u.id;
  const detail = [u.name ? (u.email ?? u.username) : null, u.path].filter(Boolean).join(' · ');
  return { title, detail };
}
